import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Package, ChevronDown, ChevronRight, Briefcase, ClipboardList, Tag, RefreshCw, Target, ArrowUpRight } from "lucide-react";
import {
  EquipmentCoverageRowView,
  EquipmentCategoryCoverageView,
  EquipmentIssuedAssetView,
  EquipmentIssueSource,
  OtherHolderView,
  UnresolvedRequirementView,
  AssetCondition,
  getEquipmentCoverage,
} from "../../../../api/General/assets";
import { getBranches, getDepartments } from "../../../../api/General/Company";
import { getBusinessRoles, BusinessRoleStatus, BusinessRoleView } from "../../../../api/General/businessRoles";
import { Branch, Department } from "../../../../models/general";
import Button from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useToast, ToastRenderer } from "@/hooks/useToast";
import AssetAccessRequiredDialog from "./AssetAccessRequiredDialog";
import CoverageAiPanel from "./CoverageAiPanel";
import type { RootState } from "../../../../store/redux/store/store";
import { useCanManageAssets } from "@/hooks/useCanManageAssets";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  PriorityChip,
  RequirementSource,
  SourceChip,
  RequirementLabel,
  WaiverBadge,
} from '@/components/assets/equipmentRequirementChrome';
import { parseApiUtc } from '@/utils/apiDate';
import { appDate } from '@/utils/appDateFormat';

interface EquipmentCoverageTabProps {
  // Switch to the Assets tab filtered to the needed category + Available so HR can issue a matching asset.
  onIssueItem: (neededItem: string) => void;
  /** Switches the parent to the Recommendations tab. NOT a route: both tabs are one component, and
      the parent's deep-link effect keys on the capability, not on searchParams, so a <Link> changed
      the URL and left the rendered tab exactly where it was. */
  onOpenRecommendations?: (categoryId?: number) => void;
}

const conditionLabelKey: Record<AssetCondition, string> = {
  [AssetCondition.Excellent]: "assets.conditionExcellent",
  [AssetCondition.Good]: "assets.conditionGood",
  [AssetCondition.Fair]: "assets.conditionFair",
  [AssetCondition.Poor]: "assets.conditionPoor",
};

// Tinted-bg + full-border + leading-icon pill (no side stripes) — matches the badge
// guardrail used elsewhere in this tab (see the under-covered count badge above).
const sourceBadgeClasses: Record<EquipmentIssueSource, string> = {
  [EquipmentIssueSource.Role]: "border-blue-200 text-blue-700 bg-blue-50",
  [EquipmentIssueSource.Request]: "border-violet-200 text-violet-700 bg-violet-50",
  [EquipmentIssueSource.Standalone]: "border-border text-muted-foreground bg-muted/50",
  [EquipmentIssueSource.Goal]: "border-emerald-200 text-emerald-700 bg-emerald-50",
};

const sourceBadgeIcon: Record<EquipmentIssueSource, React.ElementType> = {
  [EquipmentIssueSource.Role]: Briefcase,
  [EquipmentIssueSource.Request]: ClipboardList,
  [EquipmentIssueSource.Standalone]: Tag,
  [EquipmentIssueSource.Goal]: Target,
};

/**
 * Coverage-level buckets for the fourth filter.
 *
 * Deliberately pinned to the SAME thresholds as `coverageColor` below, so the filter selects exactly
 * the rows whose percentage is already painted that colour — green / amber / red. Inventing a second
 * set of cut-offs here would mean picking "Partially covered" and getting back red rows.
 *
 * `notFull` is the odd one out and is intentional: it is the bucket behind the "under-covered" badge
 * on the tab strip and in the summary line, which the server defines as `CoveragePercent < 100`
 * (AssetService.GetEquipmentCoverageAsync). It overlaps partial + critical by design — it is the
 * one-click follow-through for someone who arrived by clicking that badge.
 *
 * This is a post-computation predicate on rows the server has already scored, so unlike the other
 * three filters it needs no round-trip and no new query parameter.
 */
type CoverageLevelFilter = "all" | "full" | "partial" | "critical" | "notFull";

const coverageLevelPredicate: Record<CoverageLevelFilter, (pct: number) => boolean> = {
  all: () => true,
  full: (pct) => pct >= 100,
  partial: (pct) => pct >= 60 && pct < 100,
  critical: (pct) => pct < 60,
  notFull: (pct) => pct < 100,
};

const coverageLevelLabelKey: Record<CoverageLevelFilter, string> = {
  all: "assets.coverage.allCoverageLevels",
  full: "assets.coverage.levelFull",
  partial: "assets.coverage.levelPartial",
  critical: "assets.coverage.levelCritical",
  notFull: "assets.coverage.levelNotFull",
};

const coverageLevelOptions: CoverageLevelFilter[] = ["all", "full", "partial", "critical", "notFull"];

const EquipmentCoverageTab: React.FC<EquipmentCoverageTabProps> = ({ onIssueItem, onOpenRecommendations }) => {
  const { t } = useTranslation();
  const { toast, dismissToast } = useToast();
  // Shared derivation (tag OR Hr) — see hooks/useCanManageAssets.ts.
  const isAssetManager = useCanManageAssets();
  const companyId = useSelector((state: RootState) => state.user.company?.id as number | undefined);
  const currentRole = useSelector((state: RootState) => state.user.role);
  // AssetAi/CoverageRecommendations is Policy "Hr", which resolves to Hr or Admin. An
  // AssetManagement-tagged employee reaches this tab but would 403, so do not offer it to them.
  const canSeeCoverageAi = currentRole === "Hr" || currentRole === "Admin";
  const [rows, setRows] = useState<EquipmentCoverageRowView[]>([]);
  const [underCovered, setUnderCovered] = useState(0);
  const [waivedCount, setWaivedCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [otherHolders, setOtherHolders] = useState<OtherHolderView[]>([]);
  const [unresolved, setUnresolved] = useState<UnresolvedRequirementView[]>([]);
  const [excludedEmployees, setExcludedEmployees] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [businessRoles, setBusinessRoles] = useState<BusinessRoleView[]>([]);
  const [branchFilter, setBranchFilter] = useState<number | undefined>(undefined);
  const [departmentFilter, setDepartmentFilter] = useState<number | undefined>(undefined);
  const [businessRoleFilter, setBusinessRoleFilter] = useState<number | undefined>(undefined);
  const [coverageLevelFilter, setCoverageLevelFilter] = useState<CoverageLevelFilter>("all");

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const [branchesResult, departmentsResult, rolesResult] = await Promise.all([
        getBranches(companyId),
        getDepartments(companyId),
        getBusinessRoles({ status: BusinessRoleStatus.Active, pageSize: 100 }),
      ]);
      if (branchesResult.errors.length === 0) setBranches(branchesResult.payload);
      if (departmentsResult.errors.length === 0) setDepartments(departmentsResult.payload);
      if (rolesResult.errors.length === 0) setBusinessRoles(rolesResult.payload.items);
    })();
  }, [companyId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getEquipmentCoverage({
        branchId: branchFilter,
        departmentId: departmentFilter,
        businessRoleId: businessRoleFilter,
      });
      if (result.errors.length === 0) {
        setRows(result.payload.rows);
        setUnderCovered(result.payload.underCoveredCount);
        setWaivedCount(result.payload.waivedCount ?? 0);
        setTotal(result.payload.totalWithRequirements);
        setOtherHolders(result.payload.otherHolders ?? []);
        setUnresolved(result.payload.unresolvedRequirements ?? []);
        setExcludedEmployees(result.payload.employeesExcludedByUnresolvedRequirements ?? 0);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [branchFilter, departmentFilter, businessRoleFilter]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setBranchFilter(undefined);
    setDepartmentFilter(undefined);
    setBusinessRoleFilter(undefined);
    setCoverageLevelFilter("all");
  };

  // Applied client-side — see the CoverageLevelFilter doc comment. `rows` stays the unfiltered set so
  // the summary line can still say how many of the total are being shown.
  const visibleRows = React.useMemo(
    () => rows.filter((r) => coverageLevelPredicate[coverageLevelFilter](r.coveragePercent)),
    [rows, coverageLevelFilter]
  );

  const toggle = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const handleIssueClick = (neededItem: string) => {
    if (!isAssetManager) {
      setAccessDialogOpen(true);
      return;
    }
    onIssueItem(neededItem);
  };

  const coverageColor = (pct: number) =>
    pct >= 100 ? "text-success" : pct >= 60 ? "text-amber-600" : "text-destructive";
  const conditionColor = (pct?: number) =>
    pct == null ? "text-muted-foreground" : pct >= 80 ? "text-success" : pct >= 50 ? "text-amber-600" : "text-destructive";

  /**
   * One section of grouped requirements. Roles and goals are the same shape with a different source,
   * so they share this renderer: the two hand-written blocks it replaces had already drifted apart in
   * spacing and in whether an empty group could render at all.
   *
   * The group label links to the record it came from when the payload carries an id. Without the link
   * the drill-down names a role or a goal and then leaves the reader to go find it.
   */
  const renderRequirementSection = ({
    employeeId, testId, heading, subtitle, source, groups, fallback, divided = false,
  }: {
    employeeId: string;
    testId: string;
    heading: string;
    subtitle?: string;
    source: RequirementSource;
    groups: { key: string; label: string; href?: string; onActivate?: () => void; needs: EquipmentCategoryCoverageView[] }[];
    fallback?: React.ReactNode;
    divided?: boolean;
  }) => (
    <div className={`space-y-3${divided ? ' mt-5 pt-4 border-t border-border' : ''}`} data-testid={testId}>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{heading}</h3>
        {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {groups.length > 0 ? (
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.key} className="border border-border rounded-md p-3 bg-background">
              <div className="text-xs font-semibold text-foreground mb-2">
                {group.href ? (
                  <Link
                    to={group.href}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded"
                    data-testid={`coverage-${source}-link-${employeeId}-${group.key}`}
                  >
                    {group.label}
                    <ArrowUpRight className="h-3 w-3 rtl:-scale-x-100" aria-hidden="true" />
                  </Link>
                ) : group.onActivate ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); group.onActivate!(); }}
                    className="inline-flex items-center gap-1 text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded"
                    data-testid={`coverage-${source}-link-${employeeId}-${group.key}`}
                  >
                    {group.label}
                    <ArrowUpRight className="h-3 w-3 rtl:-scale-x-100" aria-hidden="true" />
                  </button>
                ) : (
                  group.label
                )}
              </div>
              <ul className="space-y-3">
                {group.needs.map((item) => renderNeed(employeeId, item, `${group.key}-${item.neededItem}`, source))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        fallback
      )}
    </div>
  );

  // Shared renderer for a single needed-item row, used by both the grouped-by-role
  // list and the backward-safe flat-list fallback (older payloads without roleRequirements).
  const renderNeed = (employeeId: string, item: EquipmentCategoryCoverageView, reactKey?: string, source: RequirementSource = 'role') => {
    const needed = item.quantity ?? 1;
    // issuedCount is new; fall back to isCovered for any older payload shape.
    const issued = item.issuedCount ?? (item.isCovered ? 1 : 0);
    const fullyCovered = issued >= needed && issued > 0;
    const partial = issued > 0 && issued < needed;
    // A crossed-off line is not a gap the tab is asking anyone to close, so it offers no Issue action.
    const actionable = !item.isWaived;
    return (
      <li
        key={reactKey ?? item.neededItem}
        className="space-y-1.5"
        data-testid={`coverage-item-${employeeId}-${item.neededItem}`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <RequirementLabel label={item.neededItem} state={item} />
            <SourceChip source={source} />
            <WaiverBadge state={item} />
            {item.priority && <PriorityChip priority={item.priority} />}
            {needed > 1 && (
              <Tooltip content={t("assets.coverage.qtyNeededTooltip")}>
                <span className="text-xs text-muted-foreground" data-testid={`coverage-qty-${employeeId}-${item.neededItem}`}>
                  {t("assets.coverage.qtyNeeded", { needed })}
                </span>
              </Tooltip>
            )}
          </div>
          {fullyCovered ? (
            <Tooltip content={t("assets.coverage.itemConditionTooltip")}>
              <span className={`text-xs font-medium shrink-0 ${conditionColor(item.conditionPercent)}`}>
                {t("assets.coverage.conditionLabel", { pct: item.conditionPercent ?? 0 })}
              </span>
            </Tooltip>
          ) : partial ? (
            <span className="flex items-center gap-3 shrink-0">
              <span className="text-warning text-xs font-medium" data-testid={`coverage-partial-${employeeId}-${item.neededItem}`}>
                {t("assets.coverage.issuedCount", { issued, needed })}
              </span>
              {actionable && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); handleIssueClick(item.neededItem); }}
                  data-testid={`coverage-issue-${employeeId}-${item.neededItem}`}
                >
                  {t("assets.coverage.issueMore")}
                </Button>
              )}
            </span>
          ) : (
            <span className="flex items-center gap-3 shrink-0">
              <span className={`text-xs font-medium ${actionable ? 'text-destructive' : 'text-muted-foreground'}`}>
                {t("assets.coverage.notIssued")}
              </span>
              {actionable && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); handleIssueClick(item.neededItem); }}
                  data-testid={`coverage-issue-${employeeId}-${item.neededItem}`}
                >
                  {t("assets.coverage.issueItem")}
                </Button>
              )}
            </span>
          )}
        </div>
        {item.isAddedByAssetManager && (item.addedByName || item.addedOn) && (
          <p className="text-xs text-muted-foreground" data-testid={`coverage-added-by-${employeeId}-${item.neededItem}`}>
            {t("assets.coverage.addedProvenance", {
              name: item.addedByName || t("assets.coverage.someone"),
              date: appDate(parseApiUtc(item.addedOn)),
            })}
          </p>
        )}
        {item.matchedAssets.length > 0 && (
          <ul className="ms-4 space-y-1">
            {item.matchedAssets.map((a, i) => (
              <li key={`${a.assetName}-${i}`} className="text-xs text-muted-foreground flex items-center gap-2">
                <span className="text-foreground">{a.assetName}</span>
                <span>· {t(conditionLabelKey[a.condition])} · {a.weight}</span>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  // Branch / Department / Role drill-down — always visible so a filter that empties the
  // table can still be reset. Mirrors the select/reset styling of AssetFilters.tsx.
  const filterRow = (
    <div className="flex flex-wrap gap-3 items-center" data-testid="coverage-filters">
      <select
        value={branchFilter ?? ""}
        onChange={(e) => setBranchFilter(e.target.value ? Number(e.target.value) : undefined)}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        aria-label={t("a11y.filterByBranch")}
        data-testid="coverage-filter-branch"
      >
        <option value="">{t("assets.coverage.allBranches")}</option>
        {branches.map((b) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </select>

      <select
        value={departmentFilter ?? ""}
        onChange={(e) => setDepartmentFilter(e.target.value ? Number(e.target.value) : undefined)}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        aria-label={t("a11y.filterByDepartment")}
        data-testid="coverage-filter-department"
      >
        <option value="">{t("assets.coverage.allDepartments")}</option>
        {departments.map((d) => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </select>

      <select
        value={businessRoleFilter ?? ""}
        onChange={(e) => setBusinessRoleFilter(e.target.value ? Number(e.target.value) : undefined)}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        aria-label={t("a11y.filterByRole")}
        data-testid="coverage-filter-role"
      >
        <option value="">{t("assets.coverage.allRoles")}</option>
        {businessRoles.map((r) => (
          <option key={r.id} value={r.id}>{r.name}</option>
        ))}
      </select>

      <select
        value={coverageLevelFilter}
        onChange={(e) => setCoverageLevelFilter(e.target.value as CoverageLevelFilter)}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        aria-label={t("a11y.filterByCoverageLevel")}
        data-testid="coverage-filter-level"
      >
        {coverageLevelOptions.map((level) => (
          <option key={level} value={level}>{t(coverageLevelLabelKey[level])}</option>
        ))}
      </select>

      <Tooltip content={t("assets.coverage.resetFilters")}>
        <button
          type="button"
          onClick={resetFilters}
          aria-label={t("assets.coverage.resetFilters")}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          data-testid="coverage-filters-reset"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </Tooltip>
    </div>
  );

  /**
   * Requirements the engine could not resolve.
   *
   * These used to be discarded in silence — no error, no warning, not even counted as unmet — which
   * made a broken category structure indistinguishable from a company that had simply never recorded
   * any equipment needs. That is why this renders in the EMPTY state as well: the case it exists to
   * explain is precisely the one where the table has nothing in it.
   */
  const unresolvedPanel = (unresolved.length > 0 || excludedEmployees > 0) ? (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-500/10"
      data-testid="coverage-unresolved"
    >
      <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
        {t("assets.coverage.unresolvedHeading")}
      </h2>
      <p className="mt-1 text-sm text-amber-900/80 dark:text-amber-200/80">
        {t("assets.coverage.unresolvedBody")}
      </p>
      {unresolved.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-amber-900 dark:text-amber-200">
          {unresolved.map((u) => (
            <li key={u.categoryId} data-testid={`coverage-unresolved-${u.categoryId}`}>
              {u.name ?? t("assets.coverage.unresolvedDeleted")}
              {u.level != null && (
                <span className="ms-1 text-xs opacity-80">
                  {t("assets.coverage.unresolvedLevel", { level: u.level })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {excludedEmployees > 0 && (
        <p className="mt-2 text-sm text-amber-900/80 dark:text-amber-200/80" data-testid="coverage-unresolved-excluded">
          {t("assets.coverage.unresolvedExcluded", { count: excludedEmployees })}
        </p>
      )}
      <p className="mt-2 text-xs text-amber-900/70 dark:text-amber-200/70">
        {t("assets.coverage.unresolvedFix")}
      </p>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="space-y-4">
        {filterRow}
        <div className="flex justify-center py-12" role="status" aria-label={t("common.loading")}>
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (total === 0 && otherHolders.length === 0) {
    return (
      <div className="space-y-4">
        {filterRow}
        {unresolvedPanel}
        <div className="py-12 text-center" data-testid="coverage-empty">
          <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">{t("assets.coverage.empty")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filterRow}
      {unresolvedPanel}
      {total > 0 && (
      <>
      <div className="flex items-center gap-3 text-sm">
        <span className="text-muted-foreground">
          {coverageLevelFilter === "all"
            ? t("assets.coverage.withRequirements", { count: total })
            : t("assets.coverage.showingFiltered", { shown: visibleRows.length, total })}
        </span>
        {underCovered > 0 && (
          <Tooltip content={t("assets.coverage.badgeTooltip")}>
            <span
              className="inline-flex items-center rounded-full border border-amber-400 text-amber-700 bg-amber-50 px-2.5 py-0.5 text-xs font-medium tabular-nums"
              data-testid="coverage-under-count"
            >
              {t("assets.coverage.underCovered", { count: underCovered })}
            </span>
          </Tooltip>
        )}
        {waivedCount > 0 && (
          <Tooltip content={t("assets.coverage.waivedCountTooltip")}>
            <span
              className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground"
              data-testid="coverage-waived-count"
            >
              {t("assets.coverage.waivedCount", { count: waivedCount })}
            </span>
          </Tooltip>
        )}
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="text-start font-medium px-4 py-2.5 w-8"></th>
              <th className="text-start font-medium px-4 py-2.5">{t("assets.coverage.colEmployee")}</th>
              <th className="text-start font-medium px-4 py-2.5">{t("assets.coverage.colDepartment")}</th>
              <th className="text-start font-medium px-4 py-2.5">
                <Tooltip content={t("assets.coverage.colRequirementsFromTooltip")}>
                  <span>{t("assets.coverage.colRequirementsFrom")}</span>
                </Tooltip>
              </th>
              <th className="text-end font-medium px-4 py-2.5">
                <Tooltip content={t("assets.coverage.colCoverageTooltip")}>
                  <span>{t("assets.coverage.colCoverage")}</span>
                </Tooltip>
              </th>
              <th className="text-end font-medium px-4 py-2.5">
                <Tooltip content={t("assets.coverage.colConditionTooltip")}>
                  <span>{t("assets.coverage.colCondition")}</span>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr className="border-t border-border">
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="coverage-level-empty">
                  {t("assets.coverage.noRowsForLevel")}
                </td>
              </tr>
            )}
            {visibleRows.map((r) => {
              const open = !!expanded[r.employeeId];
              return (
                <React.Fragment key={r.employeeId}>
                  <tr
                    className="border-t border-border hover:bg-muted/30 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                    onClick={() => toggle(r.employeeId)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(r.employeeId); } }}
                    tabIndex={0}
                    aria-expanded={open}
                    data-testid={`coverage-row-${r.employeeId}`}
                  >
                    <td className="px-4 py-2.5">
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-foreground">{r.employeeName || "-"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.department || "-"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground" data-testid={`coverage-sources-${r.employeeId}`}>
                      {(() => {
                        // `roleRequirements === undefined` is an OLDER payload with no per-role split;
                        // only then does the flat roleName stand in for it.
                        const hasRole = r.roleRequirements === undefined
                          ? !!r.roleName
                          : r.roleRequirements.length > 0;
                        const hasGoal = (r.goalRequirements?.length ?? 0) > 0;
                        const hasAdded = (r.addedRequirements?.needs.length ?? 0) > 0;
                        if (!hasRole && !hasGoal && !hasAdded) return "-";
                        // Kinds of source, one chip each. WHICH role, WHICH goal and who added it are
                        // named in the expansion, where each one links to its own record. Naming the
                        // role here and chipping the other two made the column read as two ideas.
                        return (
                          <span className="flex flex-wrap items-center gap-1.5">
                            {hasRole && <SourceChip source="role" />}
                            {hasGoal && <SourceChip source="goal" />}
                            {hasAdded && <SourceChip source="added" />}
                          </span>
                        );
                      })()}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-end font-semibold tabular-nums ${coverageColor(r.coveragePercent)}`}
                      data-testid={`coverage-percent-${r.employeeId}`}
                    >
                      <Tooltip content={t("assets.coverage.rowCoverageTooltip", { covered: r.items.filter((i) => i.isCovered).length, total: r.items.length })}>
                        <span>{r.coveragePercent}%</span>
                      </Tooltip>
                    </td>
                    <td
                      className={`px-4 py-2.5 text-end font-semibold tabular-nums ${conditionColor(r.conditionPercent)}`}
                      data-testid={`coverage-condition-${r.employeeId}`}
                    >
                      <Tooltip content={t("assets.coverage.rowConditionTooltip")}>
                        <span>{r.conditionPercent == null ? "-" : `${r.conditionPercent}%`}</span>
                      </Tooltip>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-muted/20 border-t border-border">
                      <td></td>
                      <td colSpan={5} className="px-4 py-3">
                        {/* Role requirements, grouped by role. The flat `items` fallback is for an
                            OLDER payload that has no roleRequirements field at all. An empty array is
                            a current payload saying "no role", and rendering the flat union under this
                            heading relabelled an employee's GOAL equipment as a role requirement. */}
                        {(r.roleRequirements === undefined || r.roleRequirements.length > 0) && renderRequirementSection({
                          employeeId: r.employeeId,
                          testId: `coverage-role-requirements-${r.employeeId}`,
                          heading: t("assets.coverage.roleRequirementsHeading"),
                          source: 'role',
                          groups: (r.roleRequirements ?? []).map((rr) => ({
                            key: rr.roleName,
                            label: rr.roleName,
                            href: rr.roleId != null ? `/core-hr/team-role-management?role=${rr.roleId}` : undefined,
                            needs: rr.needs,
                          })),
                          fallback: (
                            <ul className="space-y-3">
                              {r.items.map((item) => renderNeed(r.employeeId, item))}
                            </ul>
                          ),
                        })}

                        {/* Goal requirements — same shape, same renderer. They used to be two blocks
                            of near-identical JSX, which is how they drifted apart. */}
                        {(r.goalRequirements?.length ?? 0) > 0 && renderRequirementSection({
                          employeeId: r.employeeId,
                          testId: `coverage-goal-requirements-${r.employeeId}`,
                          heading: t("assets.coverage.goalRequirementsHeading"),
                          source: 'goal',
                          divided: true,
                          groups: (r.goalRequirements ?? []).map((gr) => ({
                            key: gr.goalTitle,
                            label: gr.goalTitle,
                            href: gr.goalId ? `/employee-impact/goals?goal=${encodeURIComponent(gr.goalId)}` : undefined,
                            needs: gr.needs,
                          })),
                        })}

                        {/* Requirements an asset manager created directly, with no role or goal behind them. */}
                        {(r.addedRequirements?.needs.length ?? 0) > 0 && renderRequirementSection({
                          employeeId: r.employeeId,
                          testId: `coverage-added-${r.employeeId}`,
                          heading: t("assets.coverage.addedRequirementsHeading"),
                          source: 'added',
                          divided: true,
                          subtitle: t("assets.coverage.addedRequirementsSubtitle"),
                          groups: [{
                            key: 'added',
                            label: t("assets.coverage.addedByAssetManager"),
                            onActivate: isAssetManager && onOpenRecommendations
                              ? () => {
                                const ids = Array.from(new Set(r.addedRequirements!.needs.map((n) => n.categoryId)));
                                onOpenRecommendations(ids.length === 1 ? ids[0] : undefined);
                              }
                              : undefined,
                            needs: r.addedRequirements!.needs,
                          }],
                        })}

                        {/* Section B — every active issuance for this employee, with a badge explaining
                            why the asset is in their hands (role requirement, request fulfillment, or
                            a standalone hand-out with no requirement behind it). Informational only. */}
                        <div className="space-y-2 mt-5 pt-4 border-t border-border" data-testid={`coverage-issued-assets-${r.employeeId}`}>
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {t("assets.coverage.issuedAssetsHeading")}
                          </h3>
                          {r.issuedAssets && r.issuedAssets.length > 0 ? (
                            <ul className="space-y-2">
                              {r.issuedAssets.map((ia: EquipmentIssuedAssetView) => {
                                const Icon = sourceBadgeIcon[ia.source];
                                return (
                                  <li
                                    key={ia.assetId}
                                    className="flex items-center justify-between gap-4"
                                    data-testid={`coverage-issued-${r.employeeId}-${ia.assetId}`}
                                  >
                                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                      <span className="font-medium text-foreground text-xs truncate">{ia.assetName}</span>
                                      <span className="text-muted-foreground text-xs truncate">{ia.categoryName}</span>
                                      <span
                                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${sourceBadgeClasses[ia.source]}`}
                                      >
                                        <Icon className="w-3 h-3" />
                                        {ia.source === EquipmentIssueSource.Role
                                          ? t("assets.coverage.source.role", { role: ia.sourceDetail })
                                          : ia.source === EquipmentIssueSource.Request
                                          ? t("assets.coverage.source.request", { detail: ia.sourceDetail })
                                          : ia.source === EquipmentIssueSource.Goal
                                          ? t("assets.coverage.source.goal", { goal: ia.sourceDetail })
                                          : t("assets.coverage.source.standalone")}
                                      </span>
                                      {ia.returnDue && (
                                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 text-amber-700 bg-amber-50 px-2 py-0.5 text-[11px] font-medium">
                                          {t("assets.coverage.returnDue", { goal: ia.returnDueGoalTitle })}
                                        </span>
                                      )}
                                    </div>
                                    <Tooltip content={t("assets.coverage.assetConditionTooltip")}>
                                      <span className={`text-xs font-semibold shrink-0 ${conditionColor(ia.conditionPercent)}`}>
                                        {ia.conditionPercent}%
                                      </span>
                                    </Tooltip>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p className="text-xs text-muted-foreground">{t("assets.coverage.noIssuedAssets")}</p>
                          )}
                        </div>

                        {/* Reinstated 2026-08-17 at Salah's request. It was removed on 2026-07-30 as a
                            duplicate of the Overview tab; unlike that tab this is grounded in THIS
                            employee's role and goal requirements. Hidden for non-HR because the
                            endpoint is Policy "Hr" (Hr or Admin) and would otherwise 403. */}
                        {canSeeCoverageAi && (
                          <CoverageAiPanel employeeId={r.employeeId} employeeName={r.employeeName} />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* Other issued equipment — employees NOT in the table above (no role requirements) who
          still hold active issuances. No overlap with the rows above, so no duplication. */}
      {otherHolders.length > 0 && (
        <div className="space-y-3" data-testid="coverage-other-holders">
          <div>
            <h2 className="text-sm font-semibold text-foreground">{t("assets.coverage.otherHoldersHeading")}</h2>
            <p className="text-xs text-muted-foreground">{t("assets.coverage.otherHoldersSubtitle")}</p>
          </div>
          <div className="border border-border rounded-lg divide-y divide-border">
            {otherHolders.map((h) => (
              <div key={h.employeeId} className="p-4 space-y-2" data-testid={`coverage-other-holder-${h.employeeId}`}>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-foreground">{h.employeeName || "-"}</span>
                  {h.department && <span className="text-muted-foreground">· {h.department}</span>}
                </div>
                <ul className="space-y-2">
                  {h.issuedAssets.map((ia: EquipmentIssuedAssetView) => {
                    const Icon = sourceBadgeIcon[ia.source];
                    return (
                      <li
                        key={ia.assetId}
                        className="flex items-center justify-between gap-4"
                        data-testid={`coverage-other-asset-${h.employeeId}-${ia.assetId}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="font-medium text-foreground text-xs truncate">{ia.assetName}</span>
                          <span className="text-muted-foreground text-xs truncate">{ia.categoryName}</span>
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${sourceBadgeClasses[ia.source]}`}>
                            <Icon className="w-3 h-3" />
                            {ia.source === EquipmentIssueSource.Request
                              ? t("assets.coverage.source.request", { detail: ia.sourceDetail })
                              : ia.source === EquipmentIssueSource.Goal
                              ? t("assets.coverage.source.goal", { goal: ia.sourceDetail })
                              : t("assets.coverage.source.standalone")}
                          </span>
                          {ia.returnDue && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 text-amber-700 bg-amber-50 px-2 py-0.5 text-[11px] font-medium">
                              {t("assets.coverage.returnDue", { goal: ia.returnDueGoalTitle })}
                            </span>
                          )}
                        </div>
                        <Tooltip content={t("assets.coverage.assetConditionTooltip")}>
                          <span className={`text-xs font-semibold shrink-0 ${conditionColor(ia.conditionPercent)}`}>
                            {ia.conditionPercent}%
                          </span>
                        </Tooltip>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <AssetAccessRequiredDialog open={accessDialogOpen} onClose={() => setAccessDialogOpen(false)} />
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default EquipmentCoverageTab;
